import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { ActivityEventDto } from '../models/activity.models';
import { PagedResult } from '../models/common.models';
import { SignalRService } from './signalr.service';

/**
 * Client for the dashboard "Recent Activity" feed. Rows come back already filtered to
 * the viewer's visibility settings; this service adds the live-refresh signal on top.
 */
@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private http = inject(HttpClient);
  private signalRService = inject(SignalRService);

  private readonly apiUrl = `${environment.apiUrl}/api/activity`;

  /**
   * Fires when the server broadcasts ActivityFeedUpdated. The signal is deliberately
   * contentless — consumers refetch through getRecentActivity so visibility filtering
   * stays server-side. Consumers should debounce: one user action can record several
   * events back to back.
   */
  private readonly _feedUpdated$ = new Subject<void>();
  readonly feedUpdated$ = this._feedUpdated$.asObservable();

  private feedUpdatesRegistered = false;

  /**
   * The look-back window is deliberately NOT sent: the API owns it (its `days`
   * parameter defaults server-side), so this client stays a dumb pager over whatever
   * the server returns.
   */
  getRecentActivity(page: number, pageSize: number): Observable<PagedResult<ActivityEventDto>> {
    return this.http.get<PagedResult<ActivityEventDto>>(this.apiUrl, {
      params: { page, pageSize },
    });
  }

  /**
   * Register for the ActivityFeedUpdated broadcast (idempotent). The hub connection is
   * normally already started by the header's notification wiring; startConnection
   * memoizes its in-flight start, so calling during that negotiation joins the same
   * connection rather than rebuilding it.
   */
  async registerFeedUpdates(): Promise<void> {
    if (this.feedUpdatesRegistered) {
      return;
    }
    this.feedUpdatesRegistered = true;

    try {
      await this.signalRService.startConnection();
      this.signalRService.on<void>('ActivityFeedUpdated', () => this._feedUpdated$.next());
    } catch (err) {
      // Live refresh is an enhancement, not a requirement — the feed still loads on
      // demand and on tab activation. Allow a later call to retry the registration.
      this.feedUpdatesRegistered = false;
      console.error('Failed to register for activity feed updates:', err);
    }
  }
}
